import { useEffect, useRef, useState } from "react";

import type { LlmProvider } from "@blog/content-schema";
import { findLlmPreset, LLM_PRESETS } from "@blog/content-schema";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from "@blog/ui";

import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { describeError } from "../lib/api-error";
import { fuzzyFilter } from "../lib/fuzzy";
import { t } from "../lib/i18n";
import { useAsync, useSite } from "../lib/hooks";

/**
 * LLM provider settings (SPEC §74, added by explicit user request).
 *
 * One card lists what is configured, one form adds or edits a profile. Presets
 * come from `@blog/content-schema` so the picker and the Worker agree on the
 * same catalog. The API key is write-only: the list shows a mask, the form
 * never receives one, and "test connection" runs server-side.
 */

interface FormState {
  /** `""` means the custom preset — no defaults to fill. */
  readonly presetId: string;
  name: string;
  baseUrl: string;
  apiKey: string;
  apiVersion: string;
  defaultModel: string;
  enabled: boolean;
}

const EMPTY_FORM: FormState = {
  presetId: "",
  name: "",
  baseUrl: "",
  apiKey: "",
  apiVersion: "",
  defaultModel: "",
  enabled: true,
};

function formFromProvider(provider: LlmProvider): FormState {
  return {
    presetId: provider.presetId ?? "",
    name: provider.name,
    baseUrl: provider.baseUrl,
    apiKey: "",
    apiVersion: provider.apiVersion ?? "",
    defaultModel: provider.defaultModel ?? "",
    enabled: provider.enabled,
  };
}

interface VerifyState {
  readonly providerId: string;
  readonly loading: boolean;
  readonly ok: boolean | null;
  readonly message: string;
}

export function LlmSettings() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);
  const siteId = site?.id;

  const providers = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.listLlmProviders(siteId);
  }, [siteId]);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editing, setEditing] = useState<LlmProvider | null>(null);
  const [clearKey, setClearKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verify, setVerify] = useState<VerifyState | null>(null);

  if (siteLoading || providers.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (providers.error !== null) {
    return (
      <Card>
        <CardContent>
          {t("llm.loadFailed")}: {providers.error}
        </CardContent>
      </Card>
    );
  }

  if (providers.data === null || siteId === undefined) {
    return (
      <Card>
        <CardContent>{t("llm.noSite")}</CardContent>
      </Card>
    );
  }

  const selectedPreset = findLlmPreset(form.presetId);
  const set = (patch: Partial<FormState>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  /** A preset pick fills the vendor defaults; everything stays editable. */
  function pickPreset(presetId: string): void {
    const preset = findLlmPreset(presetId);

    if (preset === undefined) {
      set({ presetId: "" });
      return;
    }

    set({
      presetId: preset.id,
      name: preset.id === "custom" ? "" : preset.name,
      baseUrl: preset.baseUrl,
      apiVersion: preset.apiVersion ?? "",
      defaultModel: preset.defaultModel ?? "",
    });
  }

  function startEdit(provider: LlmProvider): void {
    setEditing(provider);
    setClearKey(false);
    setForm(formFromProvider(provider));
    setMessage(null);
    setError(null);
    setVerify(null);
  }

  function startAdd(): void {
    setEditing(null);
    setClearKey(false);
    setForm(EMPTY_FORM);
    setMessage(null);
    setError(null);
  }

  async function save(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    if (form.name.trim() === "") {
      setError(t("llm.name") + " is required.");
      return;
    }

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      if (editing === null) {
        await client.createLlmProvider(siteId, {
          ...(form.presetId === "" ? {} : { presetId: form.presetId }),
          name: form.name.trim(),
          ...(form.baseUrl.trim() === "" ? {} : { baseUrl: form.baseUrl.trim() }),
          ...(form.apiKey === "" ? {} : { apiKey: form.apiKey }),
          ...(form.apiVersion.trim() === "" ? {} : { apiVersion: form.apiVersion.trim() }),
          ...(form.defaultModel.trim() === "" ? {} : { defaultModel: form.defaultModel.trim() }),
          enabled: form.enabled,
        });
      } else {
        await client.updateLlmProvider(siteId, editing.id, {
          // Custom ("") clears the stored preset link.
          presetId: form.presetId === "" ? null : form.presetId,
          name: form.name.trim(),
          baseUrl: form.baseUrl.trim(),
          // Absent keeps the stored key; empty string clears it.
          ...(clearKey ? { apiKey: "" } : form.apiKey === "" ? {} : { apiKey: form.apiKey }),
          ...(form.apiVersion.trim() === ""
            ? { apiVersion: null }
            : { apiVersion: form.apiVersion.trim() }),
          ...(form.defaultModel.trim() === ""
            ? { defaultModel: null }
            : { defaultModel: form.defaultModel.trim() }),
          enabled: form.enabled,
        });
      }

      setMessage(t("llm.saved"));
      startAdd();
      providers.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Saving the provider failed.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(provider: LlmProvider): Promise<void> {
    if (siteId === undefined || !window.confirm(t("llm.deleteConfirm", { name: provider.name }))) {
      return;
    }

    try {
      await client.deleteLlmProvider(siteId, provider.id);

      if (editing?.id === provider.id) {
        startAdd();
      }

      providers.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Deleting the provider failed.");
    }
  }

  async function test(provider: LlmProvider): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    setVerify({ providerId: provider.id, loading: true, ok: null, message: "" });

    try {
      const result = await client.verifyLlmProvider(siteId, provider.id);

      setVerify({
        providerId: provider.id,
        loading: false,
        ok: result.verified,
        message:
          result.verified && result.models.length > 0
            ? t("llm.verifyOk", { count: result.models.length })
            : result.verified
              ? t("llm.verifyOkNoModels")
              : t("llm.verifyFail", { error: result.error ?? "" }),
      });
    } catch (cause) {
      setVerify({
        providerId: provider.id,
        loading: false,
        ok: false,
        message: t("llm.verifyFail", {
          error: cause instanceof Error ? cause.message : "",
        }),
      });
    }
  }

  /**
   * Make this provider the site's default for AI assistance.
   *
   * The server enforces "at most one default per site" — promoting here
   * demotes the previous holder, so the list needs no client-side juggling.
   * Disabled providers are refused locally: the generation commands only ever
   * resolve among enabled ones.
   */
  async function setDefault(provider: LlmProvider): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    setError(null);

    try {
      await client.updateLlmProvider(siteId, provider.id, { isDefault: true });
      providers.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Setting the default model failed.");
    }
  }

  return (
    <div>
      <PageHeader title={t("llm.title")} description={t("llm.desc")} />

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("llm.configured")}</CardTitle>
            <CardDescription>
              {providers.data.length === 0
                ? t("llm.empty")
                : t("llm.countDesc", { count: providers.data.length })}
              {" "}
              {t("llm.defaultHint")}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <ul className="flex flex-col gap-2">
              {providers.data.map((provider) => {
                const preset = provider.presetId === null ? null : findLlmPreset(provider.presetId);

                return (
                  <li
                    key={provider.id}
                    className="flex flex-col gap-2 rounded-md border border-neutral-200 px-3 py-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-neutral-900">{provider.name}</span>
                      <Badge variant="secondary">{preset?.name ?? t("llm.presetCustom")}</Badge>
                      {provider.isDefault ? (
                        <Badge variant="default">{t("llm.defaultBadge")}</Badge>
                      ) : null}
                      {provider.enabled ? null : (
                        <Badge variant="outline">{t("llm.disabled")}</Badge>
                      )}
                      <span className="ml-auto flex items-center gap-2">
                        {provider.isDefault || !provider.enabled ? null : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void setDefault(provider)}
                          >
                            {t("llm.setDefault")}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void test(provider)}
                          disabled={verify?.providerId === provider.id && verify.loading}
                        >
                          {verify?.providerId === provider.id && verify.loading
                            ? t("llm.verifying")
                            : t("llm.verify")}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => startEdit(provider)}>
                          {t("llm.edit")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void remove(provider)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          {t("llm.delete")}
                        </Button>
                      </span>
                    </div>

                    <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-500">
                      <span>{provider.baseUrl}</span>
                      {provider.defaultModel !== null ? (
                        <span>
                          {t("llm.defaultModel")}: {provider.defaultModel}
                        </span>
                      ) : null}
                      <span>{provider.apiKeyMask ?? t("llm.keyNone")}</span>
                    </dl>

                    {verify !== null && verify.providerId === provider.id && !verify.loading ? (
                      <p
                        className={
                          verify.ok === true ? "text-xs text-green-700" : "text-xs text-red-600"
                        }
                        role="status"
                      >
                        {verify.message}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {editing === null ? t("llm.addTitle") : t("llm.editTitle", { name: editing.name })}
            </CardTitle>
            <CardDescription>{t("llm.presetHint")}</CardDescription>
          </CardHeader>

          <CardContent>
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <Field label={t("llm.preset")} hint={t("llm.presetHint")}>
                <select
                  className="h-9 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm"
                  value={form.presetId}
                  onChange={(event) => {
                    pickPreset(event.target.value);
                  }}
                >
                  <option value="">{t("llm.presetCustom")}</option>
                  {LLM_PRESETS.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t("llm.name")} required>
                <Input
                  value={form.name}
                  placeholder={t("llm.namePlaceholder")}
                  maxLength={100}
                  onChange={(event) => {
                    set({ name: event.target.value });
                  }}
                />
              </Field>

              <Field label={t("llm.baseUrl")} hint={t("llm.baseUrlHint")} required>
                <Input
                  value={form.baseUrl}
                  placeholder="https://api.openai.com/v1"
                  maxLength={500}
                  onChange={(event) => {
                    set({ baseUrl: event.target.value });
                  }}
                />
              </Field>

              <Field
                label={t("llm.apiKey")}
                hint={editing === null ? t("llm.apiKeyCreateHint") : t("llm.apiKeyEditHint")}
              >
                <Input
                  type="password"
                  value={form.apiKey}
                  autoComplete="off"
                  maxLength={500}
                  disabled={clearKey}
                  onChange={(event) => {
                    set({ apiKey: event.target.value });
                  }}
                />
              </Field>

              {editing !== null && editing.apiKeyMask !== null ? (
                <label className="flex items-center gap-2 text-sm text-neutral-600">
                  <input
                    type="checkbox"
                    checked={clearKey}
                    onChange={(event) => {
                      setClearKey(event.target.checked);
                    }}
                  />
                  {t("llm.clearKey")} ({editing.apiKeyMask})
                </label>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("llm.apiVersion")} hint={t("llm.apiVersionHint")}>
                  <Input
                    value={form.apiVersion}
                    maxLength={50}
                    onChange={(event) => {
                      set({ apiVersion: event.target.value });
                    }}
                  />
                </Field>

                <Field
                  label={t("llm.defaultModel")}
                  hint={editing === null ? t("llm.modelSaveHint") : undefined}
                >
                  <ModelPicker
                    value={form.defaultModel}
                    onChange={(model) => {
                      set({ defaultModel: model });
                    }}
                    suggestions={selectedPreset?.models ?? []}
                    fetchModels={
                      editing === null || siteId === undefined
                        ? null
                        : () => client.listLlmProviderModels(siteId, editing.id)
                    }
                  />
                </Field>
              </div>

              <label className="flex items-center gap-2 text-sm text-neutral-600">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(event) => {
                    set({ enabled: event.target.checked });
                  }}
                />
                {t("llm.enabled")}
              </label>

              <div className="flex items-center gap-3">
                <Button type="submit" loading={saving}>
                  {saving ? t("llm.saving") : t("llm.save")}
                </Button>
                {editing !== null ? (
                  <Button type="button" variant="outline" onClick={startAdd}>
                    {t("llm.cancel")}
                  </Button>
                ) : null}
                {message !== null && <p className="text-sm text-neutral-600">{message}</p>}
                {error !== null && <p className="text-sm text-red-600">{error}</p>}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** How many model options to render before asking for a narrower query. */
const MAX_MODEL_OPTIONS = 50;

interface RemoteModels {
  readonly status: "idle" | "loading" | "loaded" | "error";
  readonly models: readonly string[];
  readonly error: string;
}

const IDLE_REMOTE: RemoteModels = { status: "idle", models: [], error: "" };

/**
 * The default-model field as a combobox over the endpoint's live model list.
 *
 * For a saved profile the list is fetched server-side (the stored key never
 * reaches the browser) the first time the picker opens, then filtered locally
 * with a fuzzy subsequence match; the provider's preset suggestions serve
 * until then, and are the only source while the form adds a not-yet-saved
 * profile. Typing stays free-form either way — model ids appear faster than
 * any list can follow, so a value that matches nothing is kept as typed.
 */
function ModelPicker({
  value,
  onChange,
  suggestions,
  fetchModels,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly suggestions: readonly string[];
  /** Loads the endpoint's models, or `null` while the profile is unsaved. */
  readonly fetchModels: (() => Promise<readonly string[]>) | null;
}) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [remote, setRemote] = useState<RemoteModels>(IDLE_REMOTE);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Switching between providers (or into/out of add mode) swaps the fetch —
  // the cached list of the previous endpoint must not survive it.
  useEffect(() => {
    setRemote(IDLE_REMOTE);
    setOpen(false);
  }, [fetchModels]);

  async function load(): Promise<void> {
    if (fetchModels === null) {
      return;
    }

    setRemote({ status: "loading", models: [], error: "" });

    try {
      const models = await fetchModels();

      setRemote({ status: "loaded", models, error: "" });
    } catch (cause) {
      setRemote({ status: "error", models: [], error: describeError(cause) });
    }
  }

  function openList(): void {
    setOpen(true);
    setHighlighted(0);

    if (fetchModels !== null && remote.status === "idle") {
      void load();
    }
  }

  function select(model: string): void {
    onChange(model);
    setOpen(false);
  }

  const source = fetchModels === null ? suggestions : remote.status === "loaded" ? remote.models : [];
  const matches = fuzzyFilter(value, source, (model) => model);
  const visible = matches.slice(0, MAX_MODEL_OPTIONS);
  const hidden = matches.length - visible.length;

  function moveHighlight(delta: number): void {
    if (matches.length === 0) {
      return;
    }

    setHighlighted((current) => (current + delta + matches.length) % matches.length);
  }

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [highlighted, open]);

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget) === false) {
          setOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown") {
          event.preventDefault();

          if (open) {
            moveHighlight(1);
          } else {
            openList();
          }
        } else if (event.key === "ArrowUp") {
          event.preventDefault();

          if (open) {
            moveHighlight(-1);
          } else {
            openList();
          }
        } else if (event.key === "Escape" && open) {
          event.preventDefault();
          setOpen(false);
        } else if (event.key === "Enter" && open) {
          // With the list open, Enter picks or dismisses — never "submit the
          // whole settings form" by accident.
          event.preventDefault();

          const picked = visible[highlighted];
          if (picked !== undefined) {
            select(picked);
          } else {
            setOpen(false);
          }
        }
      }}
    >
      <Input
        role="combobox"
        aria-expanded={open}
        aria-controls="llm-model-listbox"
        aria-autocomplete="list"
        aria-activedescendant={
          open && visible[highlighted] !== undefined ? `llm-model-option-${highlighted}` : undefined
        }
        value={value}
        maxLength={200}
        placeholder={suggestions[0] ?? ""}
        onChange={(event) => {
          onChange(event.target.value);
          openList();
        }}
        onFocus={openList}
      />

      {open ? (
        <ul
          ref={listRef}
          id="llm-model-listbox"
          role="listbox"
          aria-label={t("llm.defaultModel")}
          className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-neutral-200 bg-white py-1 shadow-md"
        >
          {fetchModels !== null && remote.status === "loading" ? (
            <li className="px-3 py-2 text-sm text-neutral-500">{t("llm.modelLoading")}</li>
          ) : null}

          {fetchModels !== null && remote.status === "error" ? (
            <li className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-red-600">
              <span className="min-w-0 truncate">{t("llm.modelLoadFailed", { error: remote.error })}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  void load();
                }}
              >
                {t("llm.modelRetry")}
              </Button>
            </li>
          ) : null}

          {visible.map((model, index) => {
            return (
              <li
                key={model}
                id={`llm-model-option-${index}`}
                role="option"
                aria-selected={index === highlighted}
                className={`cursor-pointer px-3 py-1.5 text-sm text-neutral-800 hover:bg-neutral-100 ${
                  index === highlighted ? "bg-neutral-100" : ""
                }`}
                onMouseDown={(event) => {
                  // Keep focus on the input so its blur handler does not
                  // close the list before the click lands.
                  event.preventDefault();
                }}
                onClick={() => {
                  select(model);
                }}
              >
                {model}
              </li>
            );
          })}

          {visible.length === 0 &&
          !(fetchModels !== null && (remote.status === "loading" || remote.status === "error")) ? (
            <li className="px-3 py-2 text-sm text-neutral-500">
              {value === ""
                ? fetchModels === null
                  ? t("llm.modelSaveHint")
                  : t("llm.modelEndpointEmpty")
                : t("llm.modelNoMatch", { query: value })}
            </li>
          ) : null}

          {hidden > 0 ? (
            <li className="px-3 py-1.5 text-xs text-neutral-400">{t("llm.modelMore", { count: hidden })}</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
